// Global
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useEffect, useRef, useState } from 'react';
import { sendGTMEvent } from '@next/third-parties/google';

// Lib
import { ComponentProps } from 'lib/component-props';
import { CompositeComponents } from 'lib/templates/Feature.Dart.model';
import useExperienceEditor from 'lib/use-experience-editor';
import Parser from 'html-react-parser';

// Local
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import { tailwindVariants } from './JumpLinksTailwind';

export type JumpLinksProps = ComponentProps & CompositeComponents.JumpLinks.JumpLinksList;

export type JumpLinksItem = CompositeComponents.JumpLinks.JumpLink;

const JumpLinks = (props: JumpLinksProps): JSX.Element => {
  const { jumpLinks, title, description } = props?.fields ?? {};

  const componentRef = useRef<HTMLDivElement | null>(null);

  const [isSticky, setIsSticky] = useState(false);

  const isEE = useExperienceEditor();

  const header = typeof window !== 'undefined' ? window.document.getElementById('header') : null;

  const {
    base,
    jumpLinksWrapper,
    jumpLinkItems,
    jumpLinksTopWrapper,
    jumpLinksTitle,
    jumpLinksDescription,
    jumpLinksBottomWrapper,
    jumpLinksLabel,
    jumpLinksLabelLinkWrapper,
  } = tailwindVariants({ isSticky: isSticky });

  useEffect(() => {
    const handleScroll = () => {
      if (jumpLinks && jumpLinks.length > 0) {
        const elements = jumpLinks
          .map((jumpLink) => {
            const anchorValue = (jumpLink as JumpLinksItem).fields?.jumpLinkAnchor?.value;
            if (anchorValue?.anchor) {
              return document.getElementById(anchorValue.anchor);
            }
            return null;
          })
          .filter(Boolean);

        if (elements.length === 0) return;

        const sortedElements = elements.sort(
          (a: HTMLElement, b: HTMLElement) => a.offsetTop - b.offsetTop
        );
        const lastElement = sortedElements[sortedElements.length - 1];

        if (!lastElement || !componentRef.current) return;

        const lastElementRect = lastElement.getBoundingClientRect();
        const lastElementBottom = lastElementRect.bottom + window.scrollY;
        const jumpLinkElementRect = componentRef.current?.getBoundingClientRect();
        const jumpLinkElementTop = jumpLinkElementRect.top;
        const isInView = window.scrollY > jumpLinkElementTop && window.scrollY < lastElementBottom;

        setIsSticky(isInView);
        if (header) {
          if (isInView) {
            header.classList.add('visually-none');
          } else {
            header.classList.remove('visually-none');
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [setIsSticky, isSticky, jumpLinks, header]);

  const handleClick = (targetElement: JumpLinksItem) => {
    const target = targetElement?.fields?.jumpLinkAnchor?.value?.anchor;
    const toBeScrolled = document.getElementById(target as string);
    const jumpLinksWrapperElement = componentRef.current;

    if (toBeScrolled && jumpLinksWrapperElement) {
      const wrapperRect = jumpLinksWrapperElement.getBoundingClientRect();
      const targetRect = toBeScrolled.getBoundingClientRect();
      const scrollPosition = targetRect.top - wrapperRect.height + window.scrollY;

      window.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
      });

      toBeScrolled.focus({ preventScroll: true });
    }
    sendGTMEvent({
      event: 'link',
      type: 'jump',
      'gtm.element.dataset.gtmLinkName': targetElement?.fields?.jumpLinkAnchor?.value?.text,
      'gtm.element.dataset.gtmLinkUrl': target,
    });
  };

  return (
    <div
      className={base({ isSticky: isSticky })}
      data-component="authorable/jumplinks"
      id="jump-links"
      ref={componentRef}
    >
      <div className={jumpLinksWrapper()}>
        {(title?.value || description?.value) && (
          <div className={jumpLinksTopWrapper()}>
            <Text className={jumpLinksTitle()} encode={false} field={title} tag="h2"></Text>
            <Text
              className={jumpLinksDescription()}
              encode={false}
              field={description}
              tag="p"
            ></Text>
          </div>
        )}
        <div className={jumpLinksBottomWrapper()}>
          <ul className={jumpLinkItems()}>
            {jumpLinks?.map((jumpLinkItem: JumpLinksItem, index: number) => {
              return (
                <li key={index}>
                  {isEE && jumpLinkItem?.fields?.jumpLinkAnchor?.value?.href ? (
                    <LinkWrapper
                      field={{
                        href: 'javascript:void(0)',
                        title: jumpLinkItem?.fields?.jumpLinkAnchor?.value?.text,
                        text: jumpLinkItem?.fields?.jumpLinkAnchor?.value?.text,
                      }}
                      onClick={() => handleClick(jumpLinkItem)}
                    ></LinkWrapper>
                  ) : (
                    <LinkWrapper
                      field={{
                        href: 'javascript:void(0)',
                        title: jumpLinkItem?.fields?.jumpLinkAnchor?.value?.text,
                      }}
                      onClick={() => handleClick(jumpLinkItem)}
                      editable={false}
                      className={jumpLinksLabelLinkWrapper()}
                    >
                      <span className={jumpLinksLabel()}>
                        {Parser(jumpLinkItem?.fields?.jumpLinkAnchor?.value?.text as string)}
                      </span>
                    </LinkWrapper>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default JumpLinks;
